import { useState } from 'react'
import { Link } from 'react-router'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { formatDate } from '@/lib/format'
import { may, useActiveTenant } from '@/tenant/context'
import { Button } from '@/ui/Button'
import { Field } from '@/ui/Field'
import { Modal } from '@/ui/Modal'
import { Empty, ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'
import { idempotencyKey } from './idempotency'

/** The six reasons `admin#cancel` offers. Optional, and never required. */
const REASONS = [
  'Too expensive',
  "Didn't see results",
  'Too complicated',
  'Hired an agency',
  'Business closed or paused',
  'Something else',
] as const

/**
 * The billing screen: `admin#invoices`, `admin#dunning` and `admin#cancel`.
 *
 * **The dunning panel has no card fields, and that is a deliberate departure
 * from the approved design.** `admin#dunning` draws card number, expiry and CVC
 * inputs in AugRank's own DOM. Collecting a PAN there would put AugRank inside
 * PCI DSS scope for no benefit, and it cannot work for the case that screen
 * exists for anyway: re-registering an Indian e-mandate happens on the bank's
 * page and nowhere else. So the button fetches the provider's hosted page. The
 * design file has been corrected to match rather than left disagreeing with
 * what ships.
 *
 * **Every date the tenant is shown comes from the API.** The grace cut-off, the
 * next retry, the period end: `app/billing/dunning.py` computes them from one
 * stored timestamp, so the screen states the exact date `admin#dunning`
 * promises rather than a vague "may be suspended" it worked out itself.
 *
 * **Cancellation is one screen and one button.** No retention maze — that is
 * what the approved screen's own note asks for. The reason is offered and
 * recorded; nothing depends on answering it.
 */
export function BillingPage(): JSX.Element {
  const { role, tenant } = useActiveTenant()
  const toast = useToast()
  const manages = may(role, 'billing:manage')
  const [cancelling, setCancelling] = useState(false)
  const [reason, setReason] = useState<string>(REASONS[0])
  const [gstin, setGstin] = useState<string | null>(null)
  const [address, setAddress] = useState<string | null>(null)

  const subscription = useQuery(
    (signal) => call(() => api.GET('/api/v1/billing/subscription', { signal })),
    [],
  )
  const invoices = useQuery(
    (signal) => call(() => api.GET('/api/v1/billing/invoices', { signal })),
    [],
  )
  const tax = useQuery(
    (signal) => call(() => api.GET('/api/v1/billing/tax-details', { signal })),
    [],
  )

  const cancel = useMutation((why: string) =>
    call(() =>
      api.POST('/api/v1/billing/subscription/cancellation', {
        body: { reason: why },
        params: { header: { 'Idempotency-Key': idempotencyKey('cancel', tenant.id) } },
      }),
    ),
  )
  const resume = useMutation(() =>
    call(() =>
      api.DELETE('/api/v1/billing/subscription/cancellation', {
        params: { header: { 'Idempotency-Key': idempotencyKey('resume', tenant.id) } },
      }),
    ),
  )
  const paymentMethod = useMutation(() =>
    call(() => api.POST('/api/v1/billing/payment-method', {})),
  )
  const saveTax = useMutation((values: { gstin: string | null; billing_address: string | null }) =>
    call(() => api.PUT('/api/v1/billing/tax-details', { body: values })),
  )

  if (subscription.state.status === 'loading') return <Loading label="Loading billing" />
  if (subscription.state.status === 'error') {
    return <ErrorState failure={subscription.state.failure} onRetry={subscription.refetch} />
  }

  // `?? null` because the generated type is `SubscriptionOut | null |
  // undefined` — the field is optional in OpenAPI, so a `=== null` check
  // alone leaves `undefined` unnarrowed and every read below unsafe.
  const current = subscription.state.data.subscription ?? null

  async function openPaymentPage(): Promise<void> {
    const result = await paymentMethod.run(undefined)
    if (result.ok) window.location.assign(result.data.url)
  }

  return (
    <div className="ar-stack ar-stack--loose">
      <div className="ar-row" style={{ justifyContent: 'space-between' }}>
        <h1 className="ar-h1">Billing</h1>
        <Link className="ar-btn ar-btn--ghost ar-btn--sm" to="/billing/plans">
          See all plans
        </Link>
      </div>

      {current === null ? (
        <Empty
          title="No subscription yet"
          body="Choose a plan to start your 14-day trial. Everything is included from the first day."
          action={
            manages ? (
              <Link className="ar-btn ar-btn--primary" to="/billing/plans">
                Choose a plan
              </Link>
            ) : undefined
          }
        />
      ) : (
        <>
          {current.status === 'past_due' ? (
            <div className="ar-alert ar-alert--danger">
              <span className="ar-alert__icon" aria-hidden="true">
                ✕
              </span>
              <div className="ar-alert__body">
                <strong>We couldn&apos;t charge your payment method</strong>
                {current.last_failure_message ? ` — ${current.last_failure_message}.` : '.'} Nothing
                has stopped: your work carries on as normal until{' '}
                <strong>
                  {current.grace_ends_at ? formatDate(current.grace_ends_at) : 'soon'}
                </strong>
                .
                {current.next_retry_at
                  ? ` We will try again on ${formatDate(current.next_retry_at)}.`
                  : ''}
              </div>
            </div>
          ) : null}

          {current.cancel_at_period_end ? (
            <div className="ar-alert ar-alert--info">
              <span className="ar-alert__icon" aria-hidden="true">
                ◷
              </span>
              <div className="ar-alert__body">
                <strong>
                  Your subscription ends on{' '}
                  {current.cancellation_effective_at
                    ? formatDate(current.cancellation_effective_at)
                    : 'the end of this period'}
                </strong>{' '}
                You keep full access until then, and nothing further is charged.
              </div>
            </div>
          ) : null}

          <div className="ar-card">
            <div className="ar-card__header">
              <h3>{current.plan_name}</h3>
              <span className="ar-card__spacer" />
              <span className={`ar-badge ar-badge--${badgeTone(current.status)}`}>
                {statusLabel(current.status)}
              </span>
            </div>
            <div className="ar-card__body ar-stack">
              <div className="ar-row" style={{ justifyContent: 'space-between' }}>
                <span className="ar-muted">Price</span>
                <span className="ar-mono">{current.display} / month</span>
              </div>
              {current.trial_ends_at && current.status === 'trialing' ? (
                <div className="ar-row" style={{ justifyContent: 'space-between' }}>
                  <span className="ar-muted">Trial ends</span>
                  <span>{formatDate(current.trial_ends_at)}</span>
                </div>
              ) : null}
              {current.current_period_end ? (
                <div className="ar-row" style={{ justifyContent: 'space-between' }}>
                  <span className="ar-muted">
                    {current.cancel_at_period_end ? 'Access until' : 'Renews on'}
                  </span>
                  <span>{formatDate(current.current_period_end)}</span>
                </div>
              ) : null}
              {current.pending_plan_code ? (
                <div className="ar-row" style={{ justifyContent: 'space-between' }}>
                  <span className="ar-muted">Moving to</span>
                  <span>
                    {current.pending_plan_code}
                    {current.pending_effective_at
                      ? ` on ${formatDate(current.pending_effective_at)}`
                      : ''}
                  </span>
                </div>
              ) : null}
              {current.mandate.requires_afa ? (
                <p className="ar-subtle" style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>
                  This amount is above the ₹
                  {(current.mandate.ceiling_minor / 100).toLocaleString('en-IN')} e-mandate limit,
                  so your bank will ask you to approve each renewal with an OTP. We email you 24
                  hours before every charge.
                </p>
              ) : null}
            </div>
            {manages ? (
              <div className="ar-card__footer">
                <Button
                  tone="ghost"
                  disabled={paymentMethod.pending}
                  onClick={() => {
                    void openPaymentPage()
                  }}
                >
                  Update payment method
                </Button>
                <span className="ar-card__spacer" />
                {current.cancel_at_period_end ? (
                  <Button
                    tone="primary"
                    disabled={resume.pending}
                    onClick={() => {
                      void resume.run(undefined).then((result) => {
                        if (result.ok) {
                          toast.show({ kind: 'success', title: 'Your subscription continues.' })
                          subscription.refetch()
                        }
                      })
                    }}
                  >
                    Keep my subscription
                  </Button>
                ) : (
                  <Button
                    tone="danger"
                    onClick={() => {
                      setCancelling(true)
                    }}
                  >
                    Cancel subscription
                  </Button>
                )}
              </div>
            ) : null}
          </div>
          <InlineError
            failure={paymentMethod.state.failure ?? resume.state.failure ?? cancel.state.failure}
          />
        </>
      )}

      <TaxDetails
        loading={tax.state.status === 'loading'}
        gstin={gstin ?? tax.state.data?.gstin ?? ''}
        address={address ?? tax.state.data?.billing_address ?? ''}
        editable={manages}
        pending={saveTax.pending}
        failure={saveTax.state.failure}
        onGstin={setGstin}
        onAddress={setAddress}
        onSave={() => {
          void saveTax
            .run({
              gstin: (gstin ?? tax.state.data?.gstin ?? '') || null,
              billing_address: (address ?? tax.state.data?.billing_address ?? '') || null,
            })
            .then((result) => {
              if (result.ok) {
                toast.show({ kind: 'success', title: 'Tax details saved.' })
                tax.refetch()
              }
            })
        }}
      />

      <div className="ar-card">
        <div className="ar-card__header">
          <h3>Invoices</h3>
        </div>
        {invoices.state.status === 'loading' ? (
          <div className="ar-card__body">
            <Loading label="Loading invoices" />
          </div>
        ) : invoices.state.status === 'error' ? (
          <div className="ar-card__body">
            <ErrorState failure={invoices.state.failure} onRetry={invoices.refetch} />
          </div>
        ) : invoices.state.data.invoices.length === 0 ? (
          <div className="ar-card__body">
            <Empty
              title="No invoices yet"
              body="Your first invoice appears here once a payment clears."
            />
          </div>
        ) : (
          <>
            <div className="ar-table-wrap">
              <table className="ar-table">
                <thead>
                  <tr>
                    <th>Invoice</th>
                    <th>Period</th>
                    <th className="ar-num">Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.state.data.invoices.map((invoice) => (
                    <tr key={invoice.id}>
                      <td className="ar-mono">
                        {invoice.external_url ? (
                          <a className="ar-link" href={invoice.external_url}>
                            {invoice.number}
                          </a>
                        ) : (
                          invoice.number
                        )}
                      </td>
                      <td>{formatDate(invoice.period_start)}</td>
                      <td className="ar-num">{invoice.total_display}</td>
                      <td>
                        <span className={`ar-badge ar-badge--${invoiceTone(invoice.kind)}`}>
                          {invoice.kind === 'trial' ? 'Trial' : statusLabel(invoice.status)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="ar-card__footer">
              <Seller seller={invoices.state.data.seller} />
            </div>
          </>
        )}
      </div>

      {cancelling ? (
        <Modal
          title="Cancel your subscription"
          onClose={() => {
            setCancelling(false)
          }}
          footer={
            <>
              <Button
                tone="ghost"
                onClick={() => {
                  setCancelling(false)
                }}
              >
                Keep my subscription
              </Button>
              <span className="ar-card__spacer" />
              <Button
                tone="danger"
                disabled={cancel.pending}
                onClick={() => {
                  void cancel.run(reason).then((result) => {
                    if (result.ok) {
                      setCancelling(false)
                      toast.show({
                        kind: 'success',
                        title: 'Your subscription will end at the end of this period.',
                      })
                      subscription.refetch()
                    }
                  })
                }}
              >
                Cancel subscription
              </Button>
            </>
          }
        >
          <div className="ar-stack">
            <div className="ar-panel">
              <strong>What happens</strong>
              <ul
                className="ar-muted"
                style={{
                  margin: 'var(--ar-space-2) 0 0',
                  paddingLeft: 'var(--ar-space-5)',
                  fontSize: 'var(--ar-text-body-sm-size)',
                  display: 'grid',
                  gap: 'var(--ar-space-2)',
                }}
              >
                <li>
                  You keep full access until{' '}
                  <strong>
                    {current?.current_period_end
                      ? formatDate(current.current_period_end)
                      : 'the end of this period'}
                  </strong>{' '}
                  — the period you have paid for.
                </li>
                <li>Everything we published stays on your site. It is yours.</li>
                <li>Your rankings do not reset — we just stop working on them.</li>
                <li>No cancellation fee. No further charges.</li>
              </ul>
            </div>
            <Field label="Why are you leaving? (optional)">
              {(props) => (
                <select
                  className="ar-select"
                  id={props.id}
                  aria-describedby={props['aria-describedby']}
                  value={reason}
                  onChange={(event) => {
                    setReason(event.target.value)
                  }}
                >
                  {REASONS.map((option) => (
                    <option key={option}>{option}</option>
                  ))}
                </select>
              )}
            </Field>
            <InlineError failure={cancel.state.failure} />
          </div>
        </Modal>
      ) : null}
    </div>
  )
}

/**
 * The organisation's own GSTIN and billing address — S-D3's Organisation panel
 * fields, rendered here because this is where they are used.
 *
 * The GSTIN decides CGST + SGST against IGST on every invoice, and the server
 * checks its check character: a wrong one produces a valid-looking document
 * claiming the wrong head, which is the recipient's problem to unpick with
 * their own tax authority.
 */
function TaxDetails({
  loading,
  gstin,
  address,
  editable,
  pending,
  failure,
  onGstin,
  onAddress,
  onSave,
}: {
  loading: boolean
  gstin: string
  address: string
  editable: boolean
  pending: boolean
  failure: Parameters<typeof InlineError>[0]['failure']
  onGstin: (value: string) => void
  onAddress: (value: string) => void
  onSave: () => void
}): JSX.Element {
  return (
    <div className="ar-card">
      <div className="ar-card__header">
        <h3>Your tax details</h3>
      </div>
      <div className="ar-card__body ar-stack">
        {loading ? (
          <Loading label="Loading tax details" />
        ) : (
          <>
            <Field
              label="GSTIN"
              help="Optional. With it, your invoices carry the right tax head for input credit."
            >
              {(props) => (
                <input
                  {...props}
                  className="ar-input ar-mono"
                  value={gstin}
                  disabled={!editable}
                  placeholder="36ABFCA6453L1ZC"
                  onChange={(event) => {
                    onGstin(event.target.value)
                  }}
                />
              )}
            </Field>
            <Field label="Billing address">
              {(props) => (
                <textarea
                  id={props.id}
                  aria-describedby={props['aria-describedby']}
                  className="ar-input"
                  rows={3}
                  value={address}
                  disabled={!editable}
                  onChange={(event) => {
                    onAddress(event.target.value)
                  }}
                />
              )}
            </Field>
            <InlineError failure={failure} />
          </>
        )}
      </div>
      {editable && !loading ? (
        <div className="ar-card__footer">
          <span className="ar-card__spacer" />
          <Button tone="primary" disabled={pending} onClick={onSave}>
            Save
          </Button>
        </div>
      ) : null}
    </div>
  )
}

/**
 * The supplier block CGST Rule 46 requires on the face of a tax invoice.
 *
 * Rendered from what the API sends rather than from constants here: the legal
 * name, the registered address and the GSTIN are a legal statement that can
 * change — an address moves, a registration is re-issued — and a copy in the
 * SPA would be a second version of a document nobody would think to update.
 */
function Seller({
  seller,
}: {
  seller: {
    legal_name: string
    gstin: string
    sac_code: string
    address_lines: string[]
    cin: string
  }
}): JSX.Element {
  return (
    <span className="ar-subtle" style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>
      Issued by {seller.legal_name}, {seller.address_lines.join(', ')} · GSTIN {seller.gstin} · CIN{' '}
      {seller.cin} · SAC {seller.sac_code} · valid for input credit. This is the seller&apos;s GSTIN
      — the one above is yours, and the two are never the same number.
    </span>
  )
}

function statusLabel(status: string): string {
  const labels: Record<string, string> = {
    trialing: 'Trial',
    active: 'Active',
    past_due: 'Payment failed',
    cancelled: 'Cancelled',
    expired: 'Ended',
    paid: '✓ Paid',
    open: 'Due',
    void: 'Void',
    refunded: 'Refunded',
  }
  return labels[status] ?? status
}

function badgeTone(status: string): string {
  if (status === 'past_due') return 'danger'
  if (status === 'trialing') return 'brand'
  if (status === 'active') return 'success'
  return 'neutral'
}

function invoiceTone(kind: string): string {
  return kind === 'trial' ? 'neutral' : 'success'
}
